import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { customerApi } from '../services/api';
import { AddressDto, AddressSuggestionDto } from '../types';
import FormInput from './common/FormInput';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Suggestions = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 20;
  margin: 0;
  padding: 0.35rem;
  list-style: none;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  max-height: 260px;
  overflow-y: auto;
`;

const SuggestionItem = styled.li<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.92rem;
  color: #2d3748;
  background: ${props => (props.active ? (props.theme.primary ? `${props.theme.primary}12` : '#fff5f5') : 'transparent')};
  transition: background 0.15s;

  &:hover {
    background: ${props => (props.theme.primary ? `${props.theme.primary}12` : '#fff5f5')};
  }

  i {
    color: ${props => props.theme.primary || '#e74c3c'};
    font-size: 1rem;
    flex-shrink: 0;
  }
`;

const StatusRow = styled.div`
  padding: 0.7rem 0.75rem;
  font-size: 0.85rem;
  color: #718096;
  text-align: center;
`;

const Spinner = styled.i`
  color: ${props => props.theme.primary || '#e74c3c'};
`;

interface AddressAutocompleteProps {
  label?: string;
  placeholder?: string;
  onSelect: (address: AddressDto) => void;
}

const MIN_CHARS = 3;
const DEBOUNCE_MS = 350;

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  label = 'Buscar endereço',
  placeholder = 'Digite rua, número e cidade',
  onSelect,
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestionDto[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const requestSeq = useRef(0);

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const runSearch = (value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.trim().length < MIN_CHARS) {
      setSuggestions([]);
      setIsSearching(false);
      setIsOpen(false);
      return;
    }

    setIsSearching(true);
    setIsOpen(true);
    const seq = ++requestSeq.current;

    debounceRef.current = setTimeout(async () => {
      try {
        const results = await customerApi.autocompleteAddress(value.trim());
        // Ignora respostas fora de ordem
        if (seq !== requestSeq.current) return;
        setSuggestions(results);
        setActiveIndex(-1);
      } catch (error) {
        if (seq !== requestSeq.current) return;
        setSuggestions([]);
      } finally {
        if (seq === requestSeq.current) setIsSearching(false);
      }
    }, DEBOUNCE_MS);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    runSearch(value);
  };

  const handleSelect = async (suggestion: AddressSuggestionDto) => {
    setQuery(suggestion.description);
    setIsOpen(false);
    setSuggestions([]);
    setIsResolving(true);
    try {
      const address = await customerApi.getAddressByPlaceId(suggestion.placeId);
      onSelect(address);
    } catch (error) {
      toast.error('Não foi possível carregar esse endereço. Tente outro.');
    } finally {
      setIsResolving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        handleSelect(suggestions[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const showDropdown = isOpen && query.trim().length >= MIN_CHARS;

  return (
    <Wrapper ref={wrapperRef}>
      <FormInput
        label={label}
        type="text"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
        placeholder={placeholder}
        autoComplete="off"
        rightElement={(isSearching || isResolving) ? <Spinner className="fas fa-circle-notch fa-spin" /> : undefined}
      />

      {showDropdown && (
        <Suggestions>
          {isSearching && suggestions.length === 0 && (
            <StatusRow>Buscando endereços...</StatusRow>
          )}
          {!isSearching && suggestions.length === 0 && (
            <StatusRow>Nenhum endereço encontrado</StatusRow>
          )}
          {suggestions.map((s, index) => (
            <SuggestionItem
              key={s.placeId}
              active={index === activeIndex}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <i className="fas fa-map-marker-alt" />
              <span>{s.description}</span>
            </SuggestionItem>
          ))}
        </Suggestions>
      )}
    </Wrapper>
  );
};

export default AddressAutocomplete;
